import React, { useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  TextField,
  IconButton
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { updateTitle } from '../Network';
import {
  idKey,
  dateKey,
  titleKey,
  isSummarizedKey
} from '../MeetingsKeysList';
import DownloadModal from '../DownloadModal';

const MeetingHeader = ({ 
  meeting, 
  onMeetingUpdate, 
  onMeetingDelete, 
  onMeetingShare,
  onModalStateChange
}) => {
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  
  const handleDownloadModalOpen = () => {
    setDownloadModalOpen(true);
    onModalStateChange?.(true);
  };
  
  const handleDownloadModalClose = () => {
    setDownloadModalOpen(false);
    onModalStateChange?.(false);
  };
  
  if (!meeting) return null;

  const handleTitleChange = async (e) => {
    const newTitle = e.target.value;
    // Update the local state immediately for better UX
    onMeetingUpdate?.(meeting[idKey], { [titleKey]: newTitle });
    // Then sync with server
    await updateTitle(meeting[idKey], newTitle);
  };

  return (
    <Box
      sx={{
        background: 'linear-gradient(180deg, var(--gradientStart) 10%, var(--gradientEnd) 100%)',
        borderRadius: '0px 0px 8px 8px', // Top square, bottom rounded
        p: { xs: 2.5, sm: 3 },
        mb: 3,
        color: 'white',
        boxShadow: '0 8px 32px var(--gradientStartAlpha), 0 4px 12px rgba(0, 0, 0, 0.2)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
          zIndex: 1,
        },
        '& > *': {
          position: 'relative',
          zIndex: 2,
        },
      }}
    >
      <Stack spacing={1.5}>
        {/* Title and Date Section */}
        <Box
          sx={{
            transition: 'all 0.3s ease-in-out',
          }}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
          {/* Title Field */}
          <TextField 
            id="editableTitle" 
            value={meeting[titleKey]}
            disabled={!meeting[isSummarizedKey]}
            onChange={handleTitleChange}
            variant="standard"
            fullWidth
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
            sx={{
              '& .MuiInput-root': {
                color: 'white',
                fontSize: '1.5rem',
                fontWeight: 600,
                '&:before': {
                  borderBottomColor: 'rgba(255,255,255,0.3)',
                },
                '&:hover:before': {
                  borderBottomColor: 'rgba(255,255,255,0.6)',
                },
                '&:after': {
                  borderBottomColor: 'white',
                },
              },
              '& .MuiInput-input': {
                color: 'white',
                '&::placeholder': {
                  color: 'rgba(255,255,255,0.7)',
                },
                '&:disabled': {
                  color: 'rgba(255,255,255,0.8)',
                  WebkitTextFillColor: 'rgba(255,255,255,0.8)',
                },
              },
            }}
          />
          
          {/* Compact Date and Action Buttons */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mt: 1,
            }}
          >
            {/* Date Section */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.6)',
                }}
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  opacity: 0.8,
                  fontSize: '0.8rem',
                  fontWeight: 400,
                  letterSpacing: '0.3px',
                }}
              >
                {meeting[dateKey]}
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton 
                aria-label="download meeting" 
                onClick={handleDownloadModalOpen}
                size="small"
                sx={{ 
                  color: 'rgba(255,255,255,0.8)',
                  '&:hover': {
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
              
              <IconButton 
                aria-label="share meeting" 
                onClick={() => onMeetingShare?.(meeting[idKey])}
                size="small"
                sx={{ 
                  color: 'rgba(255,255,255,0.8)',
                  '&:hover': {
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                <ShareIcon fontSize="small" />
              </IconButton>
              
              <IconButton 
                aria-label="delete meeting" 
                onClick={() => onMeetingDelete?.(meeting[idKey])}
                size="small"
                sx={{ 
                  color: 'rgba(255,255,255,0.8)',
                  '&:hover': {
                    color: 'var(--accentRed)',
                    backgroundColor: 'rgba(255,107,107,0.1)',
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Stack>
      
      {/* Download Modal */}
      <DownloadModal
        open={downloadModalOpen}
        meetingId={meeting[idKey]}
        onClose={handleDownloadModalClose}
      />
    </Box>
  );
};

MeetingHeader.displayName = 'MeetingHeader';

export default MeetingHeader; 